/**
 * Security utilities for Power Automate MCP
 *
 * Contains sanitization, validation, and escaping functions
 * to prevent injection attacks and data leakage.
 */

import { ConfigurationError } from "./errors.js";

/**
 * Escape a string for safe use in OData filter expressions.
 * Prevents OData injection by escaping single quotes.
 *
 * @param value - The string to escape
 * @returns The escaped string safe for OData filters
 */
export function escapeOData(value: string): string {
  // OData uses doubled single quotes to escape
  return value.replace(/'/g, "''");
}

/**
 * Validate and sanitize an OData filter expression.
 * Only allows safe operators and patterns.
 *
 * @param filter - The filter expression to validate
 * @returns The validated filter (unchanged if valid)
 * @throws ConfigurationError if filter contains dangerous patterns
 */
export function validateODataFilter(filter: string): string {
  // THREAT MODEL (P2-8c): this tautology/keyword blocklist is defense-in-depth, NOT a
  // security boundary — it cannot be complete (e.g. `or startswith(name,'')` passes).
  // Real protection is that queries execute under the caller's own delegated token, so
  // an "injection" only ever widens the caller's own query against data they can already
  // see. Do not mistake this for a parser-grade guarantee or attempt an OData-parser
  // rewrite on its behalf.
  // Normalize Unicode to NFC to prevent bypass via homoglyphs or decomposed characters
  const normalized = filter.normalize("NFC");

  // Reject non-ASCII characters entirely — OData filters should be ASCII only
  if (/[^\x20-\x7E]/.test(normalized)) {
    throw new ConfigurationError(
      `Invalid OData filter: non-ASCII characters not allowed`
    );
  }

  // Block dangerous patterns that could indicate injection
  const dangerousPatterns = [
    /;\s*(drop|delete|update|insert|exec|execute)/i, // SQL-like commands
    /--/,                                             // SQL comments
    /\/\*/,                                           // Block comments
    /\bor\s+\d+\s*(=|eq|ne|lt|le|gt|ge)\s*\d+/i,    // OR 1 eq 1 / OR 1 lt 2 tautologies
    /\bor\s+['"][^'"]*['"]\s*(=|eq|ne|lt|le|gt|ge)\s*['"][^'"]*['"]/i, // OR 'a' lt 'b' tautologies
    /\)\s*or\s*\(\s*['"]?1['"]?\s*=\s*['"]?1/i,     // ) OR (1=1 pattern
    /\)\s*;\s*/,                                      // Statement terminator after paren
    /\bunion\b/i,                                     // UNION keyword
    /\bselect\b.*\bfrom\b/i,                         // SELECT...FROM pattern
    /\bor\s+true\b/i,                                 // OR true tautology
    /\bor\s+contains\s*\(\s*['"][^'"]*['"]\s*,\s*['"][^'"]*['"]\s*\)/i, // OR contains('x','x') tautology
    /\bor\s+not\s+false\b/i,                          // OR not false tautology
    /\bor\s*\(\s*\d+\s*(=|eq|ne|lt|le|gt|ge)\s*\d+\s*\)/i, // OR (1 eq 1) parenthesized
    /\bor\s*\(\s*true\s*\)/i,                          // OR (true) parenthesized
    /\bor\s*\(\s*['"][^'"]*['"]\s*(=|eq|ne|lt|le|gt|ge)\s*['"][^'"]*['"]\s*\)/i, // OR ('a' lt 'b') parenthesized
    /\bor\s+\d+\s+(add|sub|mul|div|mod)\s+\d+\s*(=|eq|ne|lt|le|gt|ge)\s*\d+/i, // OR 1 add 1 eq 2 arithmetic
    /\bor\s+(length|concat|substring|tolower|toupper|trim|indexof|year|month|day|hour|minute|second)\s*\(/i, // OR function() tautology
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(normalized)) {
      throw new ConfigurationError(
        `Invalid OData filter: potentially dangerous pattern detected`
      );
    }
  }

  // Basic structure validation - should look like valid OData
  // Allowed: comparison (eq/ne/gt/ge/lt/le), logical (and/or/not),
  // string functions (contains/startswith/endswith), property access (properties/)
  // Allow: field eq 'value', field/subfield eq 'value', etc.
  const tokenPattern = /^[a-zA-Z0-9_\s/'()[\].,@\-:=<>]+$/u;
  if (!tokenPattern.test(normalized)) {
    throw new ConfigurationError(
      `Invalid OData filter: contains disallowed characters`
    );
  }

  return normalized;
}

/**
 * Validate a tenant ID format.
 * Must be a GUID or a known alias (common, organizations, consumers).
 *
 * @param tenantId - The tenant ID to validate
 * @returns The validated tenant ID
 * @throws ConfigurationError if format is invalid
 */
export function validateTenantId(tenantId: string): string {
  const validTenantPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$|^(common|organizations|consumers)$/i;

  if (!validTenantPattern.test(tenantId)) {
    throw new ConfigurationError(
      `Invalid tenant ID format: must be a GUID or 'common'/'organizations'/'consumers'`
    );
  }

  return tenantId;
}

/**
 * Authority URL for a specific tenant, or the fallback when no usable tenant
 * is known. Silent token redemption against /common fails for some accounts
 * even though the SAME refresh token redeems fine against the tenant-specific
 * endpoint — so every acquireTokenSilent should pin the authority to the
 * account's home tenant once one is signed in. An unvalidatable tenant ID
 * falls back rather than throwing: a bad value here must degrade to the old
 * behavior, never break token acquisition outright.
 */
export function tenantAuthorityFor(
  tenantId: string | null | undefined,
  fallback: string
): string {
  if (!tenantId) return fallback;
  try {
    return `https://login.microsoftonline.com/${validateTenantId(tenantId)}`;
  } catch {
    return fallback;
  }
}

/**
 * Validate that a URL uses HTTPS.
 *
 * @param url - The URL to validate
 * @param context - Description of what the URL is for (for error messages)
 * @returns The validated URL
 * @throws ConfigurationError if URL doesn't use HTTPS
 */
export function validateHttpsUrl(url: string, context: string): string {
  if (!url.startsWith("https://")) {
    throw new ConfigurationError(
      `${context}: URL must use HTTPS, got: ${url.substring(0, 50)}...`
    );
  }
  return url;
}

/**
 * Check if a hostname is a private/internal address (SSRF protection).
 * Blocks localhost, RFC 1918 private ranges, link-local, and common internal TLDs.
 */
export function isPrivateHost(hostname: string): boolean {
  const h = hostname.toLowerCase();

  // Exact matches
  if (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "0.0.0.0" ||
    h === "::1" ||
    h === "[::1]"
  ) return true;

  // Internal TLDs
  if (h.endsWith(".local") || h.endsWith(".internal")) return true;

  // Strip brackets from IPv6 addresses
  const stripped = h.replace(/^\[|\]$/g, "");

  // IPv6-mapped IPv4 (::ffff:127.0.0.1) and IPv4-compatible IPv6 (::127.0.0.1)
  const v4EmbeddedMatch = stripped.match(/^::(?:ffff:)?(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/);
  if (v4EmbeddedMatch) {
    return isPrivateIPv4(v4EmbeddedMatch[1]!);
  }

  // Expanded IPv6 loopback (0:0:0:0:0:0:0:1, 0000:0000:...:0001)
  if (/^[0:]+1$/.test(stripped) || /^0{0,4}(:0{0,4}){6}:0{0,3}1$/.test(stripped)) return true;

  // IPv6 private ranges: ULA (fc00::/7) and link-local (fe80::/10)
  if (/^f[cd]/i.test(stripped) || /^fe80:/i.test(stripped)) return true;

  // Block octal IP notation (e.g., 0177.0.0.1 = 127.0.0.1)
  if (/^0\d+\./.test(h) || /\.0\d+/.test(h)) return true;

  // Block decimal IP notation (e.g., 2130706433 = 127.0.0.1)
  if (/^\d{8,10}$/.test(h)) return true;

  // Block hex IP notation (e.g., 0x7f000001)
  if (/^0x[0-9a-f]+$/i.test(h)) return true;

  // Standard dotted-decimal private ranges
  return isPrivateIPv4(h);
}

function isPrivateIPv4(ip: string): boolean {
  return (
    /^127\./.test(ip) ||
    /^10\./.test(ip) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(ip) ||
    /^192\.168\./.test(ip) ||
    /^169\.254\./.test(ip) ||
    ip === "0.0.0.0"
  );
}

/**
 * Sanitize error messages to remove PII and sensitive data.
 * Replaces email addresses, paths, and other sensitive patterns.
 *
 * @param message - The error message to sanitize
 * @returns The sanitized message
 */
export function sanitizeErrorMessage(message: string): string {
  let sanitized = message;

  // Remove email addresses
  sanitized = sanitized.replace(
    /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi,
    "[EMAIL]"
  );

  // Remove file paths (Windows drive, Windows UNC, and Unix)
  sanitized = sanitized.replace(
    /\\\\[\w.-]+\\[\w\\.-]+|[A-Za-z]:\\[\w\\.-]+|\/(?:Users|home|var|tmp|etc|opt|srv|private|root)\/[\w/.-]+/g,
    "[PATH]"
  );

  // Remove GUIDs (but keep a short prefix for correlation)
  sanitized = sanitized.replace(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    (match) => `${match.substring(0, 8)}...`
  );

  // Remove bearer tokens if accidentally included
  sanitized = sanitized.replace(
    /Bearer\s+[A-Za-z0-9._-]+/gi,
    "Bearer [REDACTED]"
  );

  // Remove API keys
  sanitized = sanitized.replace(
    /[?&](api[_-]?key|key|token|secret|password|sig|sv)=[^&\s]+/gi,
    (match) => match.split("=")[0] + "=[REDACTED]"
  );

  return sanitized;
}

/**
 * Validate an environment ID.
 * Must be a valid GUID or environment name pattern.
 *
 * @param envId - The environment ID to validate
 * @returns The validated environment ID
 * @throws ConfigurationError if format is invalid
 */
/**
 * Escape a string for safe use in SQLite FTS5 MATCH queries.
 * FTS5 has special syntax characters that need escaping.
 *
 * @param query - The search query to escape
 * @returns The escaped query safe for FTS5 MATCH
 */
export function escapeFts5Query(query: string): string {
  // FTS5 special characters: " AND OR NOT ( ) * ^
  // The safest approach is to wrap the query in double quotes
  // which treats it as a literal phrase match
  // First, escape any existing double quotes
  const escaped = query.replace(/"/g, '""');
  // Wrap in quotes for literal matching
  return `"${escaped}"`;
}

/**
 * Sanitize a search query by removing potentially dangerous characters
 * while keeping the query functional.
 *
 * @param query - The search query to sanitize
 * @returns The sanitized query
 */
export function sanitizeSearchQuery(query: string): string {
  // Remove characters that could be problematic
  // Keep alphanumeric, spaces, and common safe punctuation
  return query
    .replace(/['"`;\\]/g, "") // Remove quotes and SQL-dangerous chars
    .replace(/[^\w\s\-_.]/g, " ") // Replace other special chars with space
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .trim()
    .substring(0, 100); // Limit length
}

/**
 * Validate a Dataverse table logical name.
 * Must be lowercase alphanumeric with underscores, starting with a letter.
 */
export function validateLogicalName(name: string): string {
  if (!/^[a-zA-Z][a-zA-Z0-9_]{0,127}$/i.test(name)) {
    throw new ConfigurationError(
      `Invalid table logical name format: must be alphanumeric with underscores`
    );
  }
  return name;
}

/**
 * Validate a Dataverse entity set name.
 * Must be alphanumeric with underscores, typically plural.
 */
export function validateEntitySetName(name: string): string {
  if (!/^[a-zA-Z][a-zA-Z0-9_]{0,127}$/i.test(name)) {
    throw new ConfigurationError(
      `Invalid entity set name format: must be alphanumeric with underscores`
    );
  }
  return name;
}

/**
 * Validate a Dataverse row ID (GUID format).
 */
/**
 * GUID shape accepted for Dataverse/Power Platform record IDs.
 *
 * Deliberately NOT an RFC 4122 check: Dataverse mints *sequential* GUIDs whose
 * version nibble is outside 1-8 (real example: 8ae8d5b3-6989-f111-ab10-...).
 * A strict `uuid()` validator rejects genuine record IDs.
 */
export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateRowId(id: string): string {
  const guidPattern = GUID_PATTERN;
  if (!guidPattern.test(id)) {
    throw new ConfigurationError(
      `Invalid row ID format: must be a GUID`
    );
  }
  return id;
}

/**
 * Validate a Power Automate flow run ID.
 * Run IDs are not GUIDs — they are uppercase alphanumeric identifiers
 * (e.g. "085842471510537964096537047XXCU11"), typically 20-64 chars.
 */
/**
 * Validate a flow VERSION id. Versions use the same opaque
 * uppercase-alphanumeric shape as run ids (e.g. "08584247151053796409").
 */
export function validateFlowVersionId(id: string): string {
  if (!/^[A-Za-z0-9]{8,64}$/.test(id)) {
    throw new ConfigurationError("Invalid flow version ID format");
  }
  return id;
}

export function validateFlowRunId(id: string): string {
  if (typeof id !== "string" || id.length < 10 || id.length > 128) {
    throw new ConfigurationError(
      `Invalid flow run ID format: expected alphanumeric identifier (10-128 chars)`
    );
  }
  if (!/^[0-9A-Za-z]+$/.test(id)) {
    throw new ConfigurationError(
      `Invalid flow run ID format: expected alphanumeric identifier (10-128 chars)`
    );
  }
  return id;
}

/**
 * Validate a SharePoint hostname.
 * Must be *.sharepoint.com, *.sharepoint.us, or *.sharepoint.de
 */
export function validateSharePointHostname(hostname: string): string {
  const validPattern = /^[a-z0-9-]+\.sharepoint\.(com|us|de|cn)$/i;
  if (!validPattern.test(hostname)) {
    throw new ConfigurationError(
      `Invalid SharePoint hostname: must be *.sharepoint.com`
    );
  }
  return hostname;
}

/**
 * Sanitize a file path for SharePoint operations.
 * Blocks path traversal and absolute paths.
 */
export function sanitizeFilePath(filePath: string): string {
  // Block null bytes (can truncate paths in C-based runtimes)
  if (filePath.includes("\0")) {
    throw new ConfigurationError(
      `Invalid file path: null bytes not allowed`
    );
  }

  // Normalize Unicode to NFKC to collapse fullwidth characters (e.g., ．．／ → ../)
  // and remove zero-width characters that could hide traversal sequences
  let normalized = filePath.normalize("NFKC");
  // Strip zero-width, bidi control, and other invisible characters
  // U+200B-U+200F: zero-width space/joiners, U+202A-U+202E: bidi controls (incl. RLO),
  // U+2060-U+2069: word joiner + bidi isolates, U+FEFF: BOM, U+00AD: soft hyphen
  normalized = normalized.replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF\u00AD]/g, "");

  // Block URL-encoded path traversal attempts (single and double encoding)
  // %25 is percent-encoding of '%' — catches double-encoded sequences like %252e → %2e
  if (/%2e/i.test(normalized) || /%2f/i.test(normalized) || /%5c/i.test(normalized) || /%25/i.test(normalized)) {
    throw new ConfigurationError(
      `Invalid file path: URL-encoded sequences not allowed`
    );
  }

  // Remove leading/trailing slashes
  const clean = normalized.replace(/^\/+|\/+$/g, "");

  const segments = clean.split("/");
  for (const seg of segments) {
    if (seg === ".." || seg === ".") {
      throw new ConfigurationError(
        `Invalid file path: path traversal sequences not allowed`
      );
    }
    if (seg.includes("\\")) {
      throw new ConfigurationError(
        `Invalid file path: backslashes not allowed`
      );
    }
    if (/^[A-Z]:/i.test(seg)) {
      throw new ConfigurationError(
        `Invalid file path: absolute paths not allowed`
      );
    }
  }

  return segments.filter((s) => s).join("/");
}

/**
 * Validate a Microsoft Graph ID (site, list, drive, item).
 * Graph IDs are typically GUIDs or comma-separated GUIDs.
 */
export function validateGraphId(id: string, type: string): string {
  // Graph IDs: GUIDs, comma-separated GUIDs, or base64url strings. SharePoint
  // and OneDrive DRIVE ids are "b!" + base64url (e.g. "b!NVXWgAg3TEO7IEtW..."),
  // so "!" is legitimate, and "=" appears when base64 padding is kept.
  if (!/^[a-zA-Z0-9,._!=-]+$/.test(id)) {
    throw new ConfigurationError(
      `Invalid ${type} ID format: contains disallowed characters`
    );
  }
  if (id.length > 500) {
    throw new ConfigurationError(
      `Invalid ${type} ID: too long`
    );
  }
  return id;
}

/**
 * Validate an OData $expand. Unlike $select, an expand legitimately carries
 * nested options — `parentbotid($select=name)` is the standard way to pull a
 * lookup's display value — so parentheses cannot simply be banned. Grammar:
 * comma-separated navigation properties, each optionally followed by
 * `(option;option)` where an option is $select, $top, $orderby, $filter or a
 * nested $expand; $filter bodies go through validateODataFilter.
 */
export function validateODataExpand(value: string, depth = 0): string {
  if (value.length > 2000) {
    throw new ConfigurationError(`Invalid OData $expand: too long (max 2000 characters)`);
  }
  if (/[^\x20-\x7E]/.test(value)) {
    throw new ConfigurationError(`Invalid OData $expand: non-ASCII characters not allowed`);
  }
  if (depth > 2) {
    throw new ConfigurationError(`Invalid OData $expand: nested too deeply`);
  }
  // Split on top-level commas only.
  const segments: string[] = [];
  let level = 0;
  let current = "";
  for (const ch of value) {
    if (ch === "(") level += 1;
    if (ch === ")") level -= 1;
    if (level < 0) throw new ConfigurationError(`Invalid OData $expand: unbalanced parentheses`);
    if (ch === "," && level === 0) {
      segments.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  if (level !== 0) throw new ConfigurationError(`Invalid OData $expand: unbalanced parentheses`);
  segments.push(current);

  for (const raw of segments) {
    const segment = raw.trim();
    const match = /^([A-Za-z_][\w]*(?:\/[A-Za-z_][\w]*)*)(?:\((.*)\))?$/.exec(segment);
    if (!match) {
      throw new ConfigurationError(`Invalid OData $expand: contains disallowed characters`);
    }
    const inner = match[2];
    if (inner === undefined) continue;
    for (const optionRaw of inner.split(";")) {
      const option = optionRaw.trim();
      if (option === "") continue;
      const opt = /^\$(select|top|orderby|filter|expand)=(.*)$/s.exec(option);
      if (!opt) throw new ConfigurationError(`Invalid OData $expand: unsupported option '${option.slice(0, 40)}'`);
      const [, name, body] = opt as unknown as [string, string, string];
      if (name === "select" || name === "orderby") validateODataFieldList(body, `$expand ${name}`);
      else if (name === "top") {
        if (!/^\d{1,5}$/.test(body)) throw new ConfigurationError(`Invalid OData $expand: $top must be a number`);
      } else if (name === "filter") validateODataFilter(body);
      else validateODataExpand(body, depth + 1);
    }
  }
  return value;
}

/**
 * Validate OData $select/$expand/$orderby parameters.
 * Only allows column names, commas, slashes, asc/desc, and spaces.
 */
export function validateODataFieldList(value: string, paramName: string): string {
  // Allow: column names (alphanumeric + underscore), commas, forward slashes, spaces, asc/desc
  // Parentheses deliberately excluded to prevent function injection (e.g., concat(), substring())
  if (!/^[\w\s,/]+$/.test(value)) {
    throw new ConfigurationError(
      `Invalid OData ${paramName}: contains disallowed characters`
    );
  }
  if (value.length > 2000) {
    throw new ConfigurationError(
      `Invalid OData ${paramName}: too long (max 2000 characters)`
    );
  }
  return value;
}

/**
 * Validate a Dataverse environment URL.
 * Must match *.dynamics.com pattern.
 */
export function validateDataverseUrl(url: string): string {
  // Reject an explicit http:// scheme rather than silently upgrading it — callers
  // re-prepend https:// to the returned host (dataverse-api.ts), so a caller passing
  // http:// is a mistake worth surfacing, not papering over.
  if (/^http:\/\//i.test(url)) {
    throw new ConfigurationError(
      `Invalid Dataverse URL: must use https:// (http:// is not allowed)`
    );
  }
  const clean = url.replace(/^https:\/\//i, "").replace(/\/$/, "");
  if (!/^[a-z0-9.-]+\.dynamics\.com$/i.test(clean)) {
    throw new ConfigurationError(
      `Invalid Dataverse URL: must be *.dynamics.com`
    );
  }
  return clean;
}

/**
 * Validate a resource link URL for safe fetching.
 * Must be HTTPS and on a known Microsoft domain.
 */
export function validateResourceLinkUrl(uri: string): string {
  if (!uri.startsWith("https://")) {
    throw new ConfigurationError(
      `Invalid resource link: must use HTTPS`
    );
  }
  const url = new URL(uri);
  const hostname = url.hostname.toLowerCase();
  // Proper domain boundary check: hostname must equal or be a subdomain (dot-separated)
  const allowedDomains = [
    "blob.core.windows.net",
    "dynamics.com",
    "microsoft.com",
    "sharepoint.com",
    "office.com",
    "azure.com",
    "powerplatformusercontent.com",
  ];
  const isAllowed = allowedDomains.some(
    (d) => hostname === d || hostname.endsWith(`.${d}`)
  );
  if (!isAllowed) {
    throw new ConfigurationError(
      `Invalid resource link: domain not in allowlist`
    );
  }
  return uri;
}

export function validateEnvironmentId(envId: string): string {
  // GUID format
  const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  // Environment name format (alphanumeric with hyphens, no special chars)
  const namePattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,62}[a-zA-Z0-9]$|^[a-zA-Z0-9]$/;
  // Default environment format
  const defaultPattern = /^Default-[0-9a-f-]{36}$/i;

  if (!guidPattern.test(envId) && !namePattern.test(envId) && !defaultPattern.test(envId)) {
    throw new ConfigurationError(
      `Invalid environment ID format: ${envId.substring(0, 20)}...`
    );
  }

  return envId;
}
